import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { useAuth } from "../contexts/AuthContext";
import { Lock } from "lucide-react";

export default function Online() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [now, setNow] = useState(Date.now());
  const isGuest = !user || user === false;

  useEffect(() => {
    const load = () => api.get("/online").then((r) => setUsers(r.data));
    load();
    const id = setInterval(() => { load(); setNow(Date.now()); }, 30000);
    return () => clearInterval(id);
  }, []);

  // Beregn minutter siden last_seen for å velge label + prikkfarge
  const statusFor = (iso) => {
    if (!iso) return { label: "online", mins: 0, fresh: true };
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return { label: "online", mins: 0, fresh: true };
    const mins = Math.max(0, Math.floor((now - t) / 60000));
    if (mins < 10) return { label: "online nå", mins, fresh: true };
    return { label: `aktiv for ${mins} min siden`, mins, fresh: false };
  };

  return (
    <Layout withSidebar={false}>
      <h1 className="font-display text-3xl text-[var(--cream)] mb-1">{t("online_page.title")}</h1>
      <p className="text-[var(--cream-soft)] text-sm mb-4">{t("online_page.subtitle")}</p>

      {/* Antall online + aktive siste time – synlig for alle */}
      <div className="mb-6 flex items-center flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400 online-dot" />
          <span className="font-fraunces italic text-2xl text-[var(--cream)]">
            {users.filter((u) => statusFor(u.last_seen).fresh).length}
            <span className="text-[var(--cream-soft)] text-base ml-1">online nå</span>
          </span>
        </div>
        {users.filter((u) => !statusFor(u.last_seen).fresh).length > 0 && (
          <div className="flex items-center gap-2">
            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-amber-400" />
            <span className="font-fraunces italic text-lg text-[var(--cream-soft)]">
              {users.filter((u) => !statusFor(u.last_seen).fresh).length}
              <span className="text-[var(--cream-faint)] text-sm ml-1">aktive siste time</span>
            </span>
          </div>
        )}
      </div>

      {/* Login-CTA for gjester */}
      {isGuest && users.length > 0 && (
        <div className="editorial-card p-5 mb-6 flex items-center justify-between gap-4 border border-[var(--magenta)]/30">
          <div className="flex items-center gap-3">
            <Lock className="h-5 w-5 text-[var(--magenta)] shrink-0" />
            <div>
              <div className="font-fraunces italic text-lg text-[var(--cream)]">Logg inn for å se hvem</div>
              <div className="text-xs text-[var(--cream-soft)]">Brukernavn og profiler er kun synlig for medlemmer.</div>
            </div>
          </div>
          <Link to="/login" className="btn-primary text-xs whitespace-nowrap" data-testid="online-login-cta">
            Logg inn
          </Link>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {users.length === 0 ? (
          <div className="col-span-full text-[var(--cream-faint)] text-sm">{t("online_page.no_users")}.</div>
        ) : (
          users.map((u) => {
            const st = statusFor(u.last_seen);
            const dotClass = st.fresh
              ? "bg-emerald-400 online-dot"
              : "bg-amber-400";
            const CardInner = (
              <>
                <span className="relative">
                  <Avatar className={`h-10 w-10 border border-white/10 ${isGuest ? "blur-md" : ""}`}>
                    <AvatarImage src={u.avatar_url || undefined} alt="" />
                    <AvatarFallback className="bg-[var(--brand-purple)] text-black text-sm font-bold">
                      {isGuest ? "•" : u.username.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className={`absolute -right-0.5 -bottom-0.5 h-3 w-3 rounded-full ring-2 ring-[var(--noir-elev)] ${dotClass}`} />
                </span>
                <div className="min-w-0">
                  <div className={`text-sm text-[var(--cream)] truncate ${isGuest ? "blur-sm select-none" : ""}`}>
                    {isGuest ? "•••••••••" : u.username}
                  </div>
                  <div className={`text-xs ${st.fresh ? "text-[var(--cream-faint)]" : "text-amber-300/80"}`}>
                    {isGuest ? "online" : st.label}
                  </div>
                </div>
              </>
            );

            const cls = "flex items-center gap-3 rounded-lg border border-white/10 bg-[var(--noir-elev)] p-3 hover:border-[var(--brand-purple)]/40";

            return isGuest ? (
              <div key={u.id} className={`${cls} cursor-not-allowed`} data-testid={`online-card-guest-${u.id}`}>
                {CardInner}
              </div>
            ) : (
              <Link key={u.id} to={`/profile/${u.username}`} className={cls} data-testid={`online-card-${u.id}`}>
                {CardInner}
              </Link>
            );
          })
        )}
      </div>
    </Layout>
  );
}
